import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, IconButton, Snackbar, Alert, Tooltip, Card, CardContent, Avatar, Checkbox, useMediaQuery, useTheme, Divider, Chip, TextField, InputAdornment, CircularProgress, Dialog, Button, DialogContent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import { VscNewFile } from "react-icons/vsc";
import { FaFileExcel } from 'react-icons/fa';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';
import useDataLoadingState from '../hooks/useDataLoadingState';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CommonServerDataGrid from '../components/datagridComponent';
import { formatRows, toProperCase, toSentenceCase } from '../utils/formatRows';
import NoDataComponent from '../components/NoDataComponent';
import CenterLoader from './Test/components/CenterLoader';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import { APPBAR_HEIGHT } from '../utils/theme';
import LoadingOverlay from '../components/LoadingOverlay';
import SkeletonLoader from '../components/SkeletonLoader';
import { motion } from 'framer-motion';

function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4'];
    return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
    if (!name) return 'P';
    return name.trim().charAt(0).toUpperCase();
}

export default function ProductDetail2({ connectionString }) {
    const navigate = useNavigate();
    const { user, selectedCompany } = useAuth();
    const path = `${connectionString}/api`;
    const userId = user?.UserId;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(500));

    const [isFiltered, setIsFiltered] = useState(false);

    // Shared state
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [selectAll, setSelectAll] = useState(false);

    // DESKTOP state (DataGrid)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [sortModel, setSortModel] = useState([]);

    // Debug: Log filter model changes
    useEffect(() => {
        console.log('🔍 [DEBUG] filterModel changed:', filterModel);
    }, [filterModel]);

    // MOBILE state
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [debouncedMobileSearch, setDebouncedMobileSearch] = useState('');
    const [mobilePaginationModel, setMobilePaginationModel] = useState({ page: 0, pageSize: 20 });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const selectedIdsRef = useRef([]);

    // 🔥 Add loading state hook
    const { showLoadingSpinner, showEmptyState } = useDataLoadingState(loading, rows?.length > 0);

    console.log('=== COMPONENT RENDER ===');
    console.log('isSmallScreen:', isSmallScreen);
    console.log('mobileSearchQuery:', mobileSearchQuery);
    console.log('debouncedMobileSearch:', debouncedMobileSearch);
    console.log('mobilePaginationModel:', mobilePaginationModel);
    console.log('rows count:', rows.length);
    console.log('loading:', loading);

    /* ========== HELPER: Build params for API calls ========== */
    const buildMobileParams = (pageNum, searchText) => {
        const offset = pageNum * 20;
        const params = { mode: 'search', userId, companyId, offset, limit: 20 };

        if (searchText && searchText.trim()) {
            params.search = searchText;
        }

        console.log('📦 buildMobileParams created:', params);
        return params;
    };

    const buildDesktopParams = (pageNum, pageSize) => {
        const offset = pageNum * pageSize;
        const params = { mode: 'search', userId, companyId, offset, limit: pageSize };

        console.log('📦 [buildDesktopParams] filterModel.items:', filterModel.items);
        console.log('📦 [buildDesktopParams] Number of filters:', filterModel.items?.length || 0);

        if (filterModel.items && filterModel.items.length > 0) {
            filterModel.items.forEach(item => {
                console.log(`  → Filter field: ${item.field}, value: ${item.value}, operator: ${item.operator}`);

                if (item.value !== undefined && item.value !== null && item.value !== '') {
                    const value = String(item.value).trim();
                    if (value) {
                        switch (item.field) {
                            case 'itemCode': params.itemCode = value; break;
                            case 'description': params.description = value; break;
                            case 'GroupName': params.itemGroup = value; break;
                            case 'HSNNo': params.HSN = value; break;
                            case 'TaxGST': params.GST = value; break;
                            case 'OpStock': params.OpStock = value; break;
                            default: console.log(`  ⚠️ Unknown filter field: ${item.field}`); break;
                        }
                        console.log(`  ✅ Added param: ${item.field} = ${value}`);
                    }
                }
            });
        }

        if (sortModel.length > 0) {
            const sort = sortModel[0];
            let field = sort.field;
            if (field === 'GroupName') field = 'pgm.GroupName';
            else if (field === 'HSNNo') field = 'hm.HSNNo';
            else if (field === 'TaxGST') field = 'tm.GST';
            else field = `pm.${field}`;
            params.orderBy = `${field} ${sort.sort.toUpperCase()}`;
        }

        console.log('📦 [buildDesktopParams] Final params sent to API:', JSON.stringify(params, null, 2));
        return params;
    };

    /* ========== FETCH FOR DESKTOP ========== */
    const fetchDataDesktop = useCallback(async () => {
        if (isSmallScreen) return;

        console.log('🖥️ [DESKTOP] fetchDataDesktop called');
        setLoading(true);
        try {
            const params = buildDesktopParams(paginationModel.page, paginationModel.pageSize);

            const dataRes = await axios.get(`${path}/product/search`, { params });
            const fetchedRows = Array.isArray(dataRes.data) ? dataRes.data : [];
            setRows(
                formatRows(fetchedRows, {
                    formatter: toProperCase, //  default for all
                    columnFormatters: { description: toSentenceCase, },
                })
            );

            const countParams = { ...params };
            delete countParams.mode;
            delete countParams.offset;
            delete countParams.limit;
            delete countParams.orderBy;
            delete countParams.userId;

            const countRes = await axios.get(`${path}/product/count`, { params: countParams });
            const count = Number(countRes.data) || 0;
            setTotalCount(count);

            console.log(`🖥️ [DESKTOP] Fetched ${fetchedRows.length} rows, total: ${count}`);
        } catch (err) {
            console.error('🖥️ [DESKTOP] Fetch error:', err);
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [path, userId, companyId, paginationModel, filterModel, sortModel, isSmallScreen]);

    /* ========== FETCH FOR MOBILE ========== */
    const fetchDataMobile = useCallback(async () => {
        console.log('📱 [MOBILE] fetchDataMobile called');
        console.log('📱 isSmallScreen:', isSmallScreen);

        if (!isSmallScreen) {
            console.log('📱 [MOBILE] Skipping - not small screen');
            return;
        }

        setLoading(true);
        try {
            const params = buildMobileParams(mobilePaginationModel.page, debouncedMobileSearch);

            console.log('📱 [MOBILE] API URL:', `${path}/product/search`);
            console.log('📱 [MOBILE] Params:', JSON.stringify(params, null, 2));

            const dataRes = await axios.get(`${path}/product/search`, { params });
            console.log('📱 [MOBILE] API Response:', dataRes);

            const fetchedRows = Array.isArray(dataRes.data) ? dataRes.data : [];
            console.log('📱 [MOBILE] Fetched rows:', fetchedRows);
            setRows(
                formatRows(fetchedRows, {
                    formatter: toProperCase, //  default for all
                    columnFormatters: { description: toSentenceCase, },
                }))

            const countParams = { ...params };
            delete countParams.mode;
            delete countParams.offset;
            delete countParams.limit;
            delete countParams.userId;

            console.log('📱 [MOBILE] Count params:', countParams);
            const countRes = await axios.get(`${path}/product/count`, { params: countParams });
            console.log('📱 [MOBILE] Count response:', countRes);

            const count = Number(countRes.data) || 0;
            setTotalCount(count);

            console.log(`📱 [MOBILE] SUCCESS! Fetched ${fetchedRows.length} rows, total: ${count}`);
        } catch (err) {
            console.error('📱 [MOBILE] ERROR:', err);
            console.error('📱 [MOBILE] Error response:', err.response?.data);
            setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [path, userId, companyId, mobilePaginationModel, debouncedMobileSearch, isSmallScreen]);

    /* ========== TRIGGER DESKTOP FETCH ========== */
    // Dependencies: pagination changes trigger fetch, and filterModel/sortModel changes too
    // But we use JSON.stringify to avoid infinite loops from object recreation
    const filterModelString = JSON.stringify(filterModel);
    const sortModelString = JSON.stringify(sortModel);

    useEffect(() => {
        console.log('💻 [EFFECT] Desktop fetch triggered');
        if (!isSmallScreen) {
            fetchDataDesktop();
        }
    }, [paginationModel.page, paginationModel.pageSize, filterModelString, sortModelString, isSmallScreen]);

    /* ========== HANDLE FILTER CHANGE ========== */
    // 🔥 FIX: Callback that updates filter AND resets pagination in one action
    // This avoids the infinite loop where filterModel change triggers fetch which recreates it
    const handleFilterChange = (newFilterModel) => {
        console.log('🔄 [FILTER] Filter changed, resetting pagination to page 0');
        setFilterModel(newFilterModel);
        // Reset pagination immediately when filter changes
        if (!isSmallScreen) {
            setPaginationModel({ page: 0, pageSize: 50 });
        }
    };

    /* ========== TRIGGER MOBILE FETCH ON PAGINATION ========== */
    useEffect(() => {
        console.log('📱 [EFFECT] Mobile pagination changed:', mobilePaginationModel);
        if (isSmallScreen) {
            fetchDataMobile();
        }
    }, [mobilePaginationModel.page, mobilePaginationModel.pageSize, isSmallScreen, debouncedMobileSearch]);

    /* ========== DEBOUNCE MOBILE SEARCH ========== */
    useEffect(() => {
        console.log('⏱️ [DEBOUNCE] Starting 400ms timeout for:', mobileSearchQuery);

        const timer = setTimeout(() => {
            console.log('⏱️ [DEBOUNCE] Timer completed! Setting debounced search to:', mobileSearchQuery);
            setDebouncedMobileSearch(mobileSearchQuery);
            setMobilePaginationModel({ page: 0, pageSize: 20 });
            console.log('⏱️ [DEBOUNCE] Pagination reset to page 0');
        }, 400);

        return () => {
            console.log('⏱️ [DEBOUNCE] Cleared timeout');
            clearTimeout(timer);
        };
    }, [mobileSearchQuery]);

    /* ========== DELETE ========== */
    const handleDelete = async () => {
        // if (selectionModel.length === 0) return;
        const ids = selectedIdsRef.current;
        if (!ids.length) return;
        try {
            await axios.post(`${path}/product/delete`, { userId, objAryId: ids, companyId });
            setSnackbar({ open: true, message: 'Deleted successfully.', severity: 'success' });
            setSelectionModel([]);
            selectedIdsRef.current = [];
            setSelectAll(false);

            if (isSmallScreen) {
                console.log('📱 Re-fetching mobile data after delete');
                fetchDataMobile();
            } else {
                fetchDataDesktop();
            }
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            // 🔴 Case: 400 + referenced IDs (business rule failure)
            if (status === 400 && data?.referencedIds?.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });

            } else if (status === 400 && data?.error) {
                // 🔴 400 but no referencedIds array (generic validation)
                setSnackbar({ open: true, message: data.error, severity: "warning" });
            } else {
                // 🔴 unexpected / server error
                setSnackbar({ open: true, message: "Delete failed due to an unexpected error.", severity: "error" });
            }
        } finally {
            setConfirmOpen(false);
        }
    };

    /* ========== EXPORT ========== */
    const funExportData = async () => {
        try {
            const res = await axios({ url: `${path}/product/search?mode=export`, method: 'GET', responseType: 'blob', });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedFileProduct.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Export error', err);
            setSnackbar({ open: true, message: 'Export failed.', severity: 'error' });
        }
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectionModel([]);
            selectedIdsRef.current = [];
            setSelectAll(false);
        } else {
            const allIds = rows.map(row => row.Id);
            setSelectionModel(allIds);
            selectedIdsRef.current = allIds; // 🔥 freeze
            setSelectAll(true);
        }
    };

    /* ========== COLUMNS ========== */
    const columns = [
        {
            field: 'itemCode', headerName: 'Item Code', flex: 1, minWidth: 130,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography sx={{ cursor: 'pointer', color: '#1877F2', textDecoration: 'underline', fontSize: '0.85rem', }} onClick={() => navigate(`/main/editProductMaster/${params.row.Id}`)} >
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 1.2,
            minWidth: 180,
            renderCell: (params) => (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    overflow: 'hidden'
                }}>
                    <Tooltip title={params.value || ''} placement="top">
                        <Typography sx={{
                            fontSize: '0.85rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}>
                            {params.value}
                        </Typography>
                    </Tooltip>
                </Box>
            )
        },
        { field: 'GroupName', headerName: 'Group', flex: 0.8, minWidth: 120 },
        { field: 'HSNNo', headerName: 'HSN', flex: 0.5, minWidth: 100 },
        { field: 'TaxGST', headerName: 'GST %', flex: 0.5, minWidth: 80 },
        { field: 'OpStock', headerName: 'Opening Bal', flex: 0.5, minWidth: 100 },
    ];

    if (role !== 'User') {
        columns.push({
            flex: 0.1, minWidth: 80, sortable: false, filterable: false, headerAlign: 'right', align: 'right',
            renderCell: (params) => {
                const isRowSelected = selectionModel.includes(params.row.Id);
                return (
                    <Tooltip title={isRowSelected ? 'Delete Selected' : 'Select row to enable'}>
                        <span>
                            <IconButton color="error" onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                selectedIdsRef.current = selectionModel;   // 🔥 SYNC HERE
                                setPendingDeleteIds(selectionModel);
                                setConfirmOpen(true);
                            }} disabled={!isRowSelected} >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        });
    }

    /* ========== RENDER CARDS (MOBILE) ========== */
    const renderCards = () => {
        if (showLoadingSpinner) {
            return <SkeletonLoader count={3} height={100} />;
        }

        if (showEmptyState) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                        {mobileSearchQuery ? <CustomNoDataOverlay /> : <NoDataComponent />}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        Search: "{debouncedMobileSearch}" | Page: {mobilePaginationModel.page} | Total: {totalCount}
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                {rows.map(row => {
                    const isSelected = selectionModel.includes(row.Id);
                    const avatarColor = stringToColor(row.itemCode || 'Product');
                    const initials = getInitials(row.itemCode);

                    return (
                        <Card
                            key={row.Id}
                            sx={{
                                mb: 1.5,
                                boxShadow: 1,
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: '0.2s',
                                '&:hover': { boxShadow: 3 },
                                cursor: 'pointer'
                            }}
                            onClick={() => navigate(`/main/editProductMaster/${row.Id}`)}
                        >
                            {/* Gradient top strip */}
                            <Box sx={{
                                height: 5,
                                background: `linear-gradient(90deg, ${avatarColor} 0%, ${avatarColor}88 100%)`
                            }} />

                            <CardContent sx={{ py: 1.5, px: 1.5, pb: '10px !important' }}>
                                {/* Header row: Checkbox + Avatar + Item Code + Description */}
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                    {role !== 'User' && (
                                        <Checkbox
                                            size="small"
                                            sx={{ p: 0.2, mt: 0.3, mr: 0.5, flexShrink: 0 }}
                                            checked={isSelected}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectionModel(p => p.includes(row.Id) ? p.filter(id => id !== row.Id) : [...p, row.Id]);
                                            }}
                                        />
                                    )}
                                    <Avatar
                                        sx={{
                                            bgcolor: avatarColor,
                                            color: '#fff',
                                            width: 38,
                                            height: 38,
                                            fontWeight: 'bold',
                                            mr: 1.5,
                                            flexShrink: 0
                                        }}
                                    >
                                        {initials}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: '#1976d2',
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {row.itemCode || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {row.description || '—'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 0.8 }} />

                                {/* Info chips */}
                                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.5 }}>
                                    {row.HSNNo && (
                                        <Chip label={`HSN: ${row.HSNNo}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                                    )}
                                    {row.GroupName && (
                                        <Chip label={row.GroupName} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                                    )}
                                    {(row.TaxGST !== undefined && row.TaxGST !== null) && (
                                        <Chip label={`GST: ${row.TaxGST}%`} size="small" color="primary" sx={{ fontSize: '0.7rem', height: 22 }} />
                                    )}
                                    <Chip label={`Stock: ${row.OpStock ?? '0'}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}

                {/* MOBILE: Pagination Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2, mt: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={() => setMobilePaginationModel(p => ({ ...p, page: Math.max(0, p.page - 1) }))}
                        disabled={mobilePaginationModel.page === 0}
                    >
                        Prev
                    </Button>

                    <Typography variant="body2" sx={{ minWidth: '120px', textAlign: 'center' }}>
                        Page {mobilePaginationModel.page + 1} of {Math.ceil(totalCount / mobilePaginationModel.pageSize) || 1}
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        endIcon={<NavigateNextIcon />}
                        onClick={() => setMobilePaginationModel(p => ({ ...p, page: p.page + 1 }))}
                        disabled={(mobilePaginationModel.page + 1) * mobilePaginationModel.pageSize >= totalCount}
                    >
                        Next
                    </Button>
                </Box>
            </>
        );
    };

    /* ========== PREVENT ACCIDENTAL PAGE RELOADS ========== */
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isFiltered && rows.length > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isFiltered, rows.length]);

    /* ========== RENDER ========== */
    return (
        <>
            <LoadingOverlay isLoading={loading} message="Loading data..." />
            <Box sx={{
                pt: 0.7, px: { xs: 2, sm: 3, md: 4 }, display: 'flex',
                flexDirection: 'column', height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
        }}>
            {/* ── Module Header ──────────────────────────────────────────────
                Blue gradient strip + Paper bar for a consistent look.
                Title left, Export + Add buttons right.
            ────────────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 1.5 }}>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)', borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Products
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Export current list to Excel */}
                        <Tooltip title="Export to Excel">
                            <Button startIcon={<FaFileExcel />} variant="outlined" onClick={funExportData} color="success" size="small">
                                Export
                            </Button>
                        </Tooltip>
                        {/* Navigate to the Add New Product form */}
                        <Button startIcon={<AddIcon />} variant="contained" size="small" component={Link} to="/main/productMaster">
                            Add Product
                        </Button>
                    </Box>
                </Paper>
            </Box>

            {/* Content */}
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: "12px" }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 }, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* SEARCH – MOBILE ONLY */}
                    {isSmallScreen && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <TextField variant="outlined" fullWidth size="small" placeholder="Search products by name, code..." value={mobileSearchQuery} onChange={(e) => setMobileSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}

                            />
                        </Box>
                    )}

                    {/* Bulk Action Bar – MOBILE ONLY */}
                    {isSmallScreen && role !== 'User' && selectionModel.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, }} >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox checked={selectAll} onChange={toggleSelectAll} size="small" />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {selectionModel.length} selected
                                </Typography>
                            </Box>
                            <Tooltip title="Delete selected">
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    selectedIdsRef.current = selectionModel;   // 🔥 SYNC HERE
                                    setPendingDeleteIds(selectionModel);
                                    setConfirmOpen(true);
                                }} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {isSmallScreen ? (
                         <Box sx={{ width: '100%', overflowX: 'hidden', px: 1, flex: 1, overflowY: 'auto'}}>
                            {renderCards()}
                        </Box>
                    ) : (
                        // <Box sx={{ height: '81.5vh', width: '100%' }}>

                         <Box sx={{ flex: 1, width: '100%', minHeight: 0, position: 'relative' }}>
                            <LoadingOverlay isVisible={showLoadingSpinner} />
                            {showEmptyState && !isFiltered && <NoDataComponent />}
                            {/* {!showLoadingSpinner && !(showEmptyState && !isFiltered) && ( */}
                                <CommonServerDataGrid data={rows} columns={columns} getRowId={(row) => row.Id} role={role} pageSizeOptions={[50,100]} initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }} selectionModel={selectionModel} onSelectionModelChange={setSelectionModel} onPageChange={({ page, pageSize }) => { setPaginationModel({ page, pageSize }); }} onFilterChange={handleFilterChange} onSortChange={setSortModel} rowCount={totalCount} onFilterStatusChange={setIsFiltered} />
                            {/* )} */}

                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ──────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingDeleteIds.length}</Typography>
                        {' '}product record(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" size="small">Cancel</Button>
                    <Button color="error" variant="contained" size="small" startIcon={<DeleteIcon />} onClick={handleDelete}>Delete</Button>
                </Box>
            </Dialog>
        </Box>
        </>
    );
}